import { AfterViewChecked, Directive, ElementRef, Inject, NgZone, OnDestroy, OnInit, Optional } from '@angular/core';
import CreateControl from '@office-start/create-control';
import { FileType as FileTypeReact } from '@office-start/document-types';
import LeftNavControl, { LeftNavTileItem } from '@office-start/left-nav-control';
import { registerIcons } from '@uifabric/styling';
import * as React from 'react';
import { combineLatest, from, ReplaySubject, Subject, Subscription } from 'rxjs';
import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { CalloutType, FeatureCallout } from '../feature-callout/feature-callout.model';
import { FeatureCalloutService } from '../feature-callout/feature-callout.service';
import { M365PinnedAppService } from '../m365-apps/m365-pinned-apps.service';
import { DecryptResponseService } from '../service-worker/encryption/decrypt-response.service';
import { ServiceWorkerConfigService } from '../service-worker/service-worker-config.service';
import { SERVICE_WORKER_ENABLED } from '../service-worker/service-worker-enabled.injection-token';
import { ServiceWorkerStateService } from '../service-worker/service-worker-state.service';
import { SERVICE_WORKER_USER_ID_FACTORY } from '../service-worker/service-worker-user-id.injection-token';
import { LinkTarget } from '../shared/constants/link-target.enum';
import { OfficeRoute } from '../shared/constants/office-route.enum';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents, KnownReferralPlatform } from '../shared/instrumentation/instrumentation.service';
import { UserProfileService } from '../shared/instrumentation/user-profile.service';
import { FileItem, FileType } from '../shared/models/file-item.model';
import { PaintMarkerName } from '../shared/models/paint-marker-name.enum';
import { Workload } from '../shared/models/workload.model';
import { initializeFluentIcons } from '../shared/office-start/initialize-fluent-icons';
import { OfficeStartBootstrapperService } from '../shared/office-start/office-start-bootstrapper.service';
import ReactRenderer from '../shared/react/react-renderer';
import { AppLauncherService, AppLaunchResult } from '../shared/services/app-launcher.service';
import { BrowserPaintMeasurementService } from '../shared/services/browser-paint-measurement.service';
import { CreateDocumentResult, CreateDocumentResultType, CreateDocumentService } from '../shared/services/create-document.service';
import { BrowserType, DeviceDetector } from '../shared/services/device-detector.service';
import { DocumentService } from '../shared/services/document.service';
import { EventService } from '../shared/services/event.service';
import { FloodgateService } from '../shared/services/floodgate.service';
import { OfficeRouteService } from '../shared/services/office-route.service';
import { PerformanceService } from '../shared/services/performance.service';
import { ThemingService } from '../shared/services/theming.service';
import { LaunchAction } from '../shared/services/url-launcher.service';
import { WindowService } from '../shared/services/window.service';
import { WorkloadsService } from '../shared/services/workloads.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { LocService } from '../shared/shared-context/loc.service';
import { UserContextService } from '../shared/shared-context/user-context.service';

const StayingAwareLazy = React.lazy(() =>
    import(
        /*webpackChunkName: "staying-aware" */ '@office-start/staying-aware'
    )
);

const StayingAwareRQLazy = React.lazy(() =>
    import(
        /*webpackChunkName: "staying-aware-rq" */ '@office-start/staying-aware-rq'
    )
);

const StayingAwareBadgeLazy = React.lazy(() =>
    import(
        /*webpackChunkName: "staying-aware-badge" */ '@office-start/staying-aware-badge'
    )
);

@Directive({ selector: 'ohp-left-nav-react-content' })
export class LeftNavReactContentDirective extends ReactRenderer implements AfterViewChecked, OnInit, OnDestroy {
    private coreAppTiles: ReadonlyArray<LeftNavTileItem> = [];
    private otherAppTiles: ReadonlyArray<LeftNavTileItem> = [];
    private nonAppTiles: ReadonlyArray<LeftNavTileItem> = [];
    private readonly allAppsTile: LeftNavTileItem;
    private readonly workloadsPromise: Promise<Array<Workload>>;
    private readonly coreWorkloadIds: Array<string> = ['ShellWordOnline', 'ShellExcelOnline', 'ShellPowerPointOnline'];
    private readonly createPanelEnabled: boolean;
    private readonly nonAppsPromise: Promise<void>;
    private readonly darkModeSubscription: Subscription;
    private readonly defaultSelectionSubscription: Subscription;
    private isDarkMode: boolean;
    private shouldChangeIconInHighContrast: boolean;
    private readonly m365PinnedApps: Array<string>;
    private readonly m365AppsEnabled: boolean;
    private defaultSelectedTile: string;
    private leftNavCalloutPromise: Promise<boolean>;
    private shouldShowCallout: boolean;
    private stayingAwareBadgeSubscription: Subscription;
    private loggedInstrumentation: boolean;

    constructor(
        private readonly appLauncherService: AppLauncherService,
        private readonly browserPaintMeasurementService: BrowserPaintMeasurementService,
        private readonly capabilitiesService: CapabilitiesService,
        clientStartupConfigService: ClientStartupConfigService,
        private readonly contextService: ContextService,
        private readonly createDocumentService: CreateDocumentService,
        private readonly deviceDetector: DeviceDetector,
        private readonly documentService: DocumentService,
        elementRef: ElementRef,
        private readonly eventService: EventService,
        private readonly featureCalloutService: FeatureCalloutService,
        private readonly floodgateService: FloodgateService,
        globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly instrumentationService: InstrumentationService,
        private readonly locService: LocService,
        private readonly m365PinnedAppService: M365PinnedAppService,
        private readonly ngZone: NgZone,
        private readonly officeRouteService: OfficeRouteService,
        private readonly performanceService: PerformanceService,
        serviceWorkerConfigService: ServiceWorkerConfigService,
        @Inject(SERVICE_WORKER_ENABLED) serviceWorkerEnabled: boolean,
        serviceWorkerStateService: ServiceWorkerStateService,
        @Inject(SERVICE_WORKER_USER_ID_FACTORY) serviceWorkerUserIdFactory: () => Promise<string>,
        private readonly themingService: ThemingService,
        private readonly userContextService: UserContextService,
        private readonly userProfileService: UserProfileService,
        private readonly windowService: WindowService,
        private readonly workloadsService: WorkloadsService,
        private readonly officeStartBootstrapperService: OfficeStartBootstrapperService,
        @Optional() decryptResponseService?: DecryptResponseService
    ) {
        super(elementRef);
        /* tslint:disable */
        registerIcons({
            icons: {
                'homeLogo': (
                    <svg width='24' height='24' fill='none'>
                        <path fill-rule='evenodd' clip-rule='evenodd' d='M10.55 2.532a2.25 2.25 0 012.9 0l6.75 5.692c.507.428.8 1.057.8 1.72v9.803a1.75 1.75 0 01-1.75 1.75h-3.5a1.75 1.75 0 01-1.75-1.75v-5.5a.25.25 0 00-.25-.25h-3.5a.25.25 0 00-.25.25v5.5a1.75 1.75 0 01-1.75 1.75h-3.5A1.75 1.75 0 013 19.747V9.944c0-.663.293-1.292.8-1.72l6.75-5.692zm1.933 1.147a.75.75 0 00-.966 0L4.767 9.37a.75.75 0 00-.267.573v9.803c0 .138.112.25.25.25h3.5a.25.25 0 00.25-.25v-5.5c0-.966.784-1.75 1.75-1.75h3.5c.966 0 1.75.784 1.75 1.75v5.5c0 .138.112.25.25.25h3.5a.25.25 0 00.25-.25V9.944a.75.75 0 00-.267-.573l-6.75-5.692z' fill='#212121' />
                    </svg>
                ),

                'homeFillLogo': (
                    <svg width='24' height='24' fill='none'>
                        <path fill-rule='evenodd' clip-rule='evenodd' d='M10.55 2.533a2.25 2.25 0 012.9 0l6.75 5.694c.508.428.8 1.057.8 1.72v9.803a1.75 1.75 0 01-1.75 1.75h-3a1.75 1.75 0 01-1.75-1.75v-5a.75.75 0 00-.75-.75h-3.5a.75.75 0 00-.75.75v5a1.75 1.75 0 01-1.75 1.75h-3A1.75 1.75 0 013 19.75V9.947c0-.663.292-1.292.8-1.72l6.75-5.694z' fill='url(#paint0_linear)' />
                        <defs>
                            <linearGradient id='paint0_linear' x1='16.499' y1='3.31' x2='6.38' y2='19.491' gradientUnits='userSpaceOnUse'>
                                <stop offset='.011' stop-color='#FDB813' />
                                <stop offset='.286' stop-color='#E27024' />
                                <stop offset='.454' stop-color='#D74326' />
                                <stop offset='.552' stop-color='#CF4B4C' />
                                <stop offset='.645' stop-color='#C8526A' />
                                <stop offset='.73' stop-color='#C25682' />
                                <stop offset='.803' stop-color='#BD5993' />
                                <stop offset='.863' stop-color='#B95B9E' />
                                <stop offset='.901' stop-color='#B85CA3' />
                            </linearGradient>
                        </defs>
                    </svg>
                ),

                'homeDarkModeLogo': (
                    <svg width='24' height='24' fill='none'>
                        <path fill-rule='evenodd' clip-rule='evenodd' d='M10.55 2.532a2.25 2.25 0 012.9 0l6.75 5.692c.507.428.8 1.057.8 1.72v9.803a1.75 1.75 0 01-1.75 1.75h-3.5a1.75 1.75 0 01-1.75-1.75v-5.5a.25.25 0 00-.25-.25h-3.5a.25.25 0 00-.25.25v5.5a1.75 1.75 0 01-1.75 1.75h-3.5A1.75 1.75 0 013 19.747V9.944c0-.663.293-1.292.8-1.72l6.75-5.692zm1.933 1.147a.75.75 0 00-.966 0L4.767 9.37a.75.75 0 00-.267.573v9.803c0 .138.112.25.25.25h3.5a.25.25 0 00.25-.25v-5.5c0-.966.784-1.75 1.75-1.75h3.5c.966 0 1.75.784 1.75 1.75v5.5c0 .138.112.25.25.25h3.5a.25.25 0 00.25-.25V9.944a.75.75 0 00-.267-.573l-6.75-5.692z' fill='#fff' />
                    </svg>
                 ),

                'createNewLogo': (
                    <svg width='24' height='24' fill='none'>
                        <path d='M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 1.5a8.5 8.5 0 100 17 8.5 8.5 0 000-17zM12 7a.75.75 0 01.75.75v3.5h3.5a.75.75 0 010 1.5h-3.5v3.5a.75.75 0 01-1.5 0v-3.5h-3.5a.75.75 0 010-1.5h3.5v-3.5A.75.75 0 0112 7z' fill='#212121' />
                    </svg>
                ),

                'createNewFillLogo': (
                    <svg width='24' height='24' fill='none'>
                        <path d='M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 5a.75.75 0 00-.743.648l-.007.102v3.5h-3.5a.75.75 0 00-.102 1.493l.102.007h3.5v3.5a.75.75 0 001.493.102l.007-.102v-3.5h3.5a.75.75 0 00.102-1.493l-.102-.007h-3.5v-3.5A.75.75 0 0012 7z' fill='url(#paint0_linear)' />
                        <defs>
                            <linearGradient id='paint0_linear' x1='16.999' y1='3.341' x2='7.001' y2='20.659' gradientUnits='userSpaceOnUse'>
                                <stop offset='.011' stop-color='#FDB813' />
                                <stop offset='.286' stop-color='#E27024' />
                                <stop offset='.454' stop-color='#D74326' />
                                <stop offset='.552' stop-color='#CF4B4C' />
                                <stop offset='.645' stop-color='#C8526A' />
                                <stop offset='.73' stop-color='#C25682' />
                                <stop offset='.803' stop-color='#BD5993' />
                                <stop offset='.863' stop-color='#B95B9E' />
                                <stop offset='.901' stop-color='#B85CA3' />
                            </linearGradient>
                        </defs>
                    </svg>
                ),

                'createDarkModeLogo': (
                    <svg width='24' height='24' fill='none'>
                        <path d='M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 1.5a8.5 8.5 0 100 17 8.5 8.5 0 000-17zM12 7a.75.75 0 01.75.75v3.5h3.5a.75.75 0 010 1.5h-3.5v3.5a.75.75 0 01-1.5 0v-3.5h-3.5a.75.75 0 010-1.5h3.5v-3.5A.75.75 0 0112 7z' fill='#fff' />
                    </svg>
                ),

                'allAppsLogo': (
                    <svg width='24' height='24' fill='none'>
                        <path d='M18.492 2.33l3.179 3.18a2.25 2.25 0 010 3.181l-2.584 2.584A2.25 2.25 0 0121 13.5v5.25A2.25 2.25 0 0118.75 21H5.25A2.25 2.25 0 013 18.75V5.25A2.25 2.25 0 015.25 3h5.25a2.25 2.25 0 012.225 1.915L15.31 2.33a2.25 2.25 0 013.182 0zM4.5 18.75c0 .414.336.75.75.75h5.999l.001-6.75H4.5v6zm8.249.75h6.001a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75h-6.001v6.75zM10.5 4.5H5.25a.75.75 0 00-.75.75v6h6.75v-6a.75.75 0 00-.75-.75zm2.25 4.81v1.94h1.94l-1.94-1.94zm3.62-5.919L13.193 6.57a.75.75 0 000 1.06l3.179 3.18a.75.75 0 001.06 0l3.18-3.18a.75.75 0 000-1.06L17.43 3.39a.75.75 0 00-1.06 0z' fill='#212121' />
                    </svg>
                ),

                'allAppsFillLogo': (
                    <svg width='24' height='24' fill='none'>
                        <path d='M18.492 2.33l3.179 3.18a2.25 2.25 0 010 3.181l-2.423 2.423A2.501 2.501 0 0121 13.5v5a2.5 2.5 0 01-2.5 2.5h-13A2.5 2.5 0 013 18.5v-13A2.5 2.5 0 015.5 3h5c1.121 0 2.07.738 2.387 1.754L15.31 2.33a2.25 2.25 0 013.182 0zM11 13H5v5.5a.5.5 0 00.5.5H11v-6zm7.5 0H13v6h5.5a.5.5 0 00.5-.5v-5a.5.5 0 00-.5-.5zm-4.06-2L13 9.56V11h1.44zM10.5 5h-5a.5.5 0 00-.5.5V11h6V5.5a.5.5 0 00-.5-.5z' fill='url(#paint0_linear)' />
                        <defs>
                            <linearGradient id='paint0_linear' x1='17.497' y1='2.968' x2='7.834' y2='19.704' gradientUnits='userSpaceOnUse'>
                                <stop offset='.011' stop-color='#FDB813' />
                                <stop offset='.286' stop-color='#E27024' />
                                <stop offset='.454' stop-color='#D74326' />
                                <stop offset='.552' stop-color='#CF4B4C' />
                                <stop offset='.645' stop-color='#C8526A' />
                                <stop offset='.73' stop-color='#C25682' />
                                <stop offset='.803' stop-color='#BD5993' />
                                <stop offset='.863' stop-color='#B95B9E' />
                                <stop offset='.901' stop-color='#B85CA3' />
                            </linearGradient>
                        </defs>
                    </svg>
                ),

                'allAppsDarkModeLogo': (
                    <svg width='24' height='24' fill='none'>
                        <path d='M18.492 2.33l3.179 3.18a2.25 2.25 0 010 3.181l-2.584 2.584A2.25 2.25 0 0121 13.5v5.25A2.25 2.25 0 0118.75 21H5.25A2.25 2.25 0 013 18.75V5.25A2.25 2.25 0 015.25 3h5.25a2.25 2.25 0 012.225 1.915L15.31 2.33a2.25 2.25 0 013.182 0zM4.5 18.75c0 .414.336.75.75.75h5.999l.001-6.75H4.5v6zm8.249.75h6.001a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75h-6.001v6.75zM10.5 4.5H5.25a.75.75 0 00-.75.75v6h6.75v-6a.75.75 0 00-.75-.75zm2.25 4.81v1.94h1.94l-1.94-1.94zm3.62-5.919L13.193 6.57a.75.75 0 000 1.06l3.179 3.18a.75.75 0 001.06 0l3.18-3.18a.75.75 0 000-1.06L17.43 3.39a.75.75 0 00-1.06 0z' fill='#fff' />
                    </svg>
                ),

                'wordMinimalLogo': (
                    <svg width='20' height='22' fill='none' xmlns='http://www.w3.org/2000/svg' xmlnsXlink='http://www.w3.org/1999/xlink'>
                        <g clip-path='url(#clip0)'>
                            <g opacity='.2'>
                                <path fill='url(#pattern0)' d='M0 .162h19.13v21.854H0z'/>
                                <path opacity='.2' d='M17.374 20.567H1.757c-.553 0-.976-.414-.976-.955V1.146c0-.541.423-.955.976-.955h15.617c.553 0 .976.414.976.955v18.466c0 .51-.456.955-.976.955z' fill='#fff'/>
                            </g>
                            <g opacity='.12'>
                                <path fill='url(#pattern1)' d='M.459 0H18.73v21.013H.459z'/>
                                <path opacity='.12' d='M17.373 20.567H1.758c-.553 0-.976-.414-.976-.955V1.146c0-.541.423-.955.976-.955h15.617c.553 0 .976.414.976.955v18.466c0 .51-.456.955-.977.955z' fill='#fff'/>
                            </g>
                            <path d='M1.757 20.567h15.617c.553 0 .976-.414.976-.955v-4.139H.78v4.14c0 .509.424.954.977.954z' fill='#103F91'/>
                            <path d='M17.374.191H1.757c-.553 0-.976.414-.976.955v4.14H18.35v-4.14c0-.541-.456-.955-.976-.955z' fill='#41A5EE'/>
                            <path d='M18.35 10.38H.78v5.093h17.57V10.38z' fill='#185ABD'/>
                            <path d='M18.35 5.285H.78v5.094h17.57V5.285z' fill='#2B7CD3'/>
                        </g>
                        <defs>
                            <pattern id='pattern0' patternContentUnits='objectBoundingBox' width='1' height='1'>
                                <use xlinkHref='#image0'/>
                            </pattern>
                            <pattern id='pattern1' patternContentUnits='objectBoundingBox' width='1' height='1'>
                                <use xlinkHref='#image1'/>
                            </pattern>
                            <image id='image0' xlinkHref='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAPoAAAEkCAYAAAAGryzWAAAACXBIWXMAAC4jAAAuIwF4pT92AAAA'/>
                            <image id='image1' xlinkHref='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAO4AAAEXCAYAAACqBHhHAAAACXBIWXMAAC4jAAAuIwF4pT92AAAA'/>
                            <clipPath id='clip0'>
                                <path fill='#fff' d='M0 0h19.13v22H0z'/>
                            </clipPath>
                        </defs>
                    </svg>
                ),

                'powerPointMinimalLogo': (
                    <svg width='22' height='22' fill='none' xmlns='http://www.w3.org/2000/svg' xmlnsXlink='http://www.w3.org/1999/xlink'>
                        <g clip-path='url(#clip0)'>
                            <g opacity='.2'>
                                <path fill='url(#pattern0)' d='M0 .157h21.835v21.835H0z' />
                                <path opacity='.2' d='M10.905 20.478c-5.58 0-10.144-4.565-10.144-10.144S5.326.19 10.905.19c5.58 0 10.144 4.565 10.144 10.144 0 5.58-4.565 10.144-10.144 10.144z' fill='#fff' />
                            </g>
                            <g opacity='.12'>
                                <path fill='url(#pattern1)' d='M.451 0h20.998v20.922H.451z' />
                                <path opacity='.12' d='M10.905 20.478C5.325 20.478.76 15.913.76 10.334S5.326.19 10.905.19c5.58 0 10.144 4.565 10.144 10.144 0 5.58-4.565 10.144-10.144 10.144z' fill='#fff' />
                            </g>
                            <path d='M10.905 20.478c-5.58 0-10.144-4.565-10.144-10.144S5.326.19 10.905.19c5.58 0 10.144 4.565 10.144 10.144 0 5.58-4.565 10.144-10.144 10.144z' fill='#D35230' />
                            <path d='M10.905.19C5.325.19.761 4.755.761 10.334h10.144V.19z' fill='#ED6C47' />
                            <path d='M10.905.19c5.58 0 10.144 4.565 10.144 10.144H10.905V.19z' fill='#FF8F6B' />
                        </g>
                        <defs>
                            <pattern id='pattern0' patternContentUnits='objectBoundingBox' width='1' height='1'>
                                <use xlinkHref='#image0'/>
                            </pattern>
                            <pattern id='pattern1' patternContentUnits='objectBoundingBox' width='1' height='1'>
                                <use xlinkHref='#image1'/>
                            </pattern>
                            <image id='image0' xlinkHref='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAASQAAAEkCAYAAACG+UzsAAAACXBIWXMAAC4jAAAuIwF4pT92AAAA'/>
                            <image id='image1' xlinkHref='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAARsAAAEXCAYAAACd7+BaAAAACXBIWXMAAC4jAAAuIwF4pT92AAAA'/>
                            <clipPath id='clip0'>
                                <path fill='#fff' d='M0 0h21.841v22H0z' />
                            </clipPath>
                        </defs>
                    </svg>
                ),

                'excelMinimalLogo': (
                    <svg width='21' height='22' fill='none' xmlns='http://www.w3.org/2000/svg' xmlnsXlink='http://www.w3.org/1999/xlink'>
                        <g clip-path='url(#clip0)'>
                            <g opacity='.2'>
                                <path fill='url(#pattern0)' d='M0 .161h20.087v21.835H0z' />
                                <path opacity='.2' d='M18.208 20.51H1.811c-.581 0-1.025-.412-1.025-.95V1.172c0-.539.444-.951 1.025-.951h16.397c.58 0 1.025.412 1.025.95V19.56c0 .539-.444.951-1.025.951z' fill='#fff' />
                            </g>
                            <g opacity='.12'>
                                <path fill='url(#pattern1)' d='M.482 0h19.185v20.998H.482z' />
                                <path opacity='.12' d='M18.208 20.51H1.81c-.58 0-1.024-.412-1.024-.95V1.172c0-.539.444-.951 1.024-.951h16.398c.58 0 1.025.412 1.025.95V19.56c0 .539-.444.951-1.025.951z' fill='#fff' />
                            </g>
                            <path d='M10.351.222h-8.54c-.581 0-1.025.412-1.025.95v4.122h9.565V.222z' fill='#21A366' />
                            <path d='M18.208.222h-7.857v5.072h8.882V1.173c0-.507-.444-.951-1.025-.951z' fill='#33C481' />
                            <path d='M19.233 10.366H10.35v5.072h8.882v-5.072z' fill='#107C41' />
                            <path d='M10.351 15.438v-5.072H.786v9.193c0 .539.444.951 1.025.951h16.397c.58 0 1.025-.412 1.025-.95v-4.122h-8.882z' fill='#185C37' />
                            <path d='M10.351 5.294H.786v5.072h9.565V5.294z' fill='#107C41' />
                            <path d='M19.233 5.294H10.35v5.072h8.882V5.294z' fill='#21A366' />
                        </g>
                        <defs>
                            <pattern id='pattern0' patternContentUnits='objectBoundingBox' width='1' height='1'>
                                <use xlinkHref='#image0'/>
                            </pattern>
                            <pattern id='pattern1' patternContentUnits='objectBoundingBox' width='1' height='1'>
                                <use xlinkHref='#image1'/>
                            </pattern>
                            <image id='image0' xlinkHref='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAPoAAAEkCAYAAAAGryzWAAAACXBIWXMAAC4jAAAuIwF4pT92AAAA'/>
                            <image id='image1' xlinkHref='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAO4AAAEXCAYAAACqBHhHAAAACXBIWXMAAC4jAAAuIwF4pT92AAAA'/>
                            <clipPath id='clip0'>
                                <path fill='#fff' d='M0 0h20.087v22H0z' />
                            </clipPath>
                        </defs>
                    </svg>
                ),
                'spacesLogo': (
                    <svg width="24" height="24" fill="none">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M13.057 7.431a2.5 2.5 0 113.536 3.536 2.5 2.5 0 01-3.536-3.536zm2.475 1.06a1 1 0 10-1.414 1.415 1 1 0 001.414-1.414z" fill="#212121"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M21.509 4.323a2.75 2.75 0 00-1.811-1.81l-.663-.206a6.75 6.75 0 00-6.773 1.674l-.996.995a3.498 3.498 0 00-4.57.327L5.455 6.545a.75.75 0 000 1.061l1.591 1.591-.18.18a1.75 1.75 0 000 2.475l.496.495-1.396.796a.75.75 0 00-.158 1.182l3.889 3.889a.75.75 0 001.181-.158l.798-1.395.497.497a1.75 1.75 0 002.475 0l.177-.177 1.59 1.59a.75.75 0 001.06 0l1.242-1.242a3.498 3.498 0 00.328-4.568l.998-.998a6.75 6.75 0 001.673-6.776l-.206-.664zm-2.256-.378c.393.122.701.43.823.823l.207.664a5.25 5.25 0 01-1.302 5.27l-5.395 5.396a.25.25 0 01-.353 0L7.926 10.79a.25.25 0 010-.354l5.396-5.396a5.25 5.25 0 015.269-1.302l.662.206zm-1.289 9.896l-2.08 2.08 1.06 1.06.712-.713c.658-.658.76-1.661.308-2.427zM7.758 6.364a1.998 1.998 0 012.428-.308l-2.08 2.08-1.06-1.06.712-.712zm2.818 9.198L8.46 13.447l-.898.512 2.5 2.5.514-.897z" fill="#212121"/>
                        <path d="M6.69 18.395a.75.75 0 00-1.06-1.061l-2.476 2.475a.75.75 0 101.061 1.06l2.475-2.474zM4.745 15.39a.75.75 0 010 1.06l-1.06 1.06a.75.75 0 11-1.061-1.06l1.06-1.06a.75.75 0 011.061 0zm3.887 4.951a.75.75 0 00-1.06-1.06l-1.059 1.058a.75.75 0 001.06 1.06l1.06-1.058z" fill="#212121"/>
                    </svg>
                ),
                'spacesFillLogo': (
                    <svg width="24" height="24" fill="none">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M13.057 7.431a2.5 2.5 0 113.536 3.536 2.5 2.5 0 01-3.536-3.536zm2.475 1.06a1 1 0 10-1.414 1.415 1 1 0 001.414-1.414z" fill="url(#paint0_linear)"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M19.698 2.512a2.75 2.75 0 011.81 1.81l.207.665a6.75 6.75 0 01-1.673 6.776l-.998.998a3.498 3.498 0 01-.328 4.568l-1.242 1.242a.75.75 0 01-1.06 0l-1.59-1.59-.177.177a1.75 1.75 0 01-2.475 0l-.487-.487-.811 1.39a.75.75 0 01-1.178.153l-3.89-3.89a.75.75 0 01.146-1.173l1.384-.829-.47-.47a1.75 1.75 0 010-2.475l.18-.18-1.592-1.59a.75.75 0 010-1.062l1.243-1.242a3.498 3.498 0 014.569-.327l.996-.995a6.75 6.75 0 016.773-1.674l.663.205zm.378 2.256a1.25 1.25 0 00-.823-.823l-.662-.206a5.25 5.25 0 00-5.269 1.302l-5.396 5.396a.25.25 0 000 .354l5.307 5.307a.25.25 0 00.353 0l5.396-5.396a5.25 5.25 0 001.3-5.27l-.206-.664z" fill="url(#paint1_linear)"/>
                        <path d="M6.69 18.395a.75.75 0 00-1.06-1.061l-2.476 2.475a.75.75 0 101.061 1.06l2.475-2.474z" fill="url(#paint2_linear)"/>
                        <path d="M4.745 15.39a.75.75 0 010 1.06l-1.06 1.06a.75.75 0 11-1.061-1.06l1.06-1.06a.75.75 0 011.061 0z" fill="url(#paint3_linear)"/>
                        <path d="M8.632 20.341a.75.75 0 00-1.06-1.06l-1.059 1.058a.75.75 0 001.06 1.06l1.06-1.058z" fill="url(#paint4_linear)"/>
                        <defs>
                            <linearGradient id="paint0_linear" x1="17.115" y1="3.319" x2="7.308" y2="20.303" gradientUnits="userSpaceOnUse">
                                <stop offset=".011" stop-color="#FDB813"/>
                                <stop offset=".286" stop-color="#E27024"/>
                                <stop offset=".454" stop-color="#D74326"/>
                                <stop offset=".552" stop-color="#CF4B4C"/>
                                <stop offset=".645" stop-color="#C8526A"/>
                                <stop offset=".73" stop-color="#C25682"/>
                                <stop offset=".803" stop-color="#BD5993"/>
                                <stop offset=".863" stop-color="#B95B9E"/>
                                <stop offset=".901" stop-color="#B85CA3"/>
                            </linearGradient>
                            <linearGradient id="paint1_linear" x1="17.115" y1="3.319" x2="7.308" y2="20.303" gradientUnits="userSpaceOnUse">
                                <stop offset=".011" stop-color="#FDB813"/>
                                <stop offset=".286" stop-color="#E27024"/>
                                <stop offset=".454" stop-color="#D74326"/>
                                <stop offset=".552" stop-color="#CF4B4C"/>
                                <stop offset=".645" stop-color="#C8526A"/>
                                <stop offset=".73" stop-color="#C25682"/>
                                <stop offset=".803" stop-color="#BD5993"/>
                                <stop offset=".863" stop-color="#B95B9E"/>
                                <stop offset=".901" stop-color="#B85CA3"/>
                            </linearGradient>
                            <linearGradient id="paint2_linear" x1="17.115" y1="3.319" x2="7.308" y2="20.303" gradientUnits="userSpaceOnUse">
                                <stop offset=".011" stop-color="#FDB813"/>
                                <stop offset=".286" stop-color="#E27024"/>
                                <stop offset=".454" stop-color="#D74326"/>
                                <stop offset=".552" stop-color="#CF4B4C"/>
                                <stop offset=".645" stop-color="#C8526A"/>
                                <stop offset=".73" stop-color="#C25682"/>
                                <stop offset=".803" stop-color="#BD5993"/>
                                <stop offset=".863" stop-color="#B95B9E"/>
                                <stop offset=".901" stop-color="#B85CA3"/>
                            </linearGradient>
                            <linearGradient id="paint3_linear" x1="17.115" y1="3.319" x2="7.308" y2="20.303" gradientUnits="userSpaceOnUse">
                                <stop offset=".011" stop-color="#FDB813"/>
                                <stop offset=".286" stop-color="#E27024"/>
                                <stop offset=".454" stop-color="#D74326"/>
                                <stop offset=".552" stop-color="#CF4B4C"/>
                                <stop offset=".645" stop-color="#C8526A"/>
                                <stop offset=".73" stop-color="#C25682"/>
                                <stop offset=".803" stop-color="#BD5993"/>
                                <stop offset=".863" stop-color="#B95B9E"/>
                                <stop offset=".901" stop-color="#B85CA3"/>
                            </linearGradient>
                            <linearGradient id="paint4_linear" x1="17.115" y1="3.319" x2="7.308" y2="20.303" gradientUnits="userSpaceOnUse">
                                <stop offset=".011" stop-color="#FDB813"/>
                                <stop offset=".286" stop-color="#E27024"/>
                                <stop offset=".454" stop-color="#D74326"/>
                                <stop offset=".552" stop-color="#CF4B4C"/>
                                <stop offset=".645" stop-color="#C8526A"/>
                                <stop offset=".73" stop-color="#C25682"/>
                                <stop offset=".803" stop-color="#BD5993"/>
                                <stop offset=".863" stop-color="#B95B9E"/>
                                <stop offset=".901" stop-color="#B85CA3"/>
                            </linearGradient>
                        </defs>
                    </svg>
                ),
                'spacesDarkModeLogo': (
                    <svg width="24" height="24" fill="none">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M13.057 7.431a2.5 2.5 0 113.536 3.536 2.5 2.5 0 01-3.536-3.536zm2.475 1.06a1 1 0 10-1.414 1.415 1 1 0 001.414-1.414z" fill="#fff"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M21.509 4.323a2.75 2.75 0 00-1.811-1.81l-.663-.206a6.75 6.75 0 00-6.773 1.674l-.996.995a3.498 3.498 0 00-4.57.327L5.455 6.545a.75.75 0 000 1.061l1.591 1.591-.18.18a1.75 1.75 0 000 2.475l.496.495-1.396.796a.75.75 0 00-.158 1.182l3.889 3.889a.75.75 0 001.181-.158l.798-1.395.497.497a1.75 1.75 0 002.475 0l.177-.177 1.59 1.59a.75.75 0 001.06 0l1.242-1.242a3.498 3.498 0 00.328-4.568l.998-.998a6.75 6.75 0 001.673-6.776l-.206-.664zm-2.256-.378c.393.122.701.43.823.823l.207.664a5.25 5.25 0 01-1.302 5.27l-5.395 5.396a.25.25 0 01-.353 0L7.926 10.79a.25.25 0 010-.354l5.396-5.396a5.25 5.25 0 015.269-1.302l.662.206zm-1.289 9.896l-2.08 2.08 1.06 1.06.712-.713c.658-.658.76-1.661.308-2.427zM7.758 6.364a1.998 1.998 0 012.428-.308l-2.08 2.08-1.06-1.06.712-.712zm2.818 9.198L8.46 13.447l-.898.512 2.5 2.5.514-.897z" fill="#fff"/>
                        <path d="M6.69 18.395a.75.75 0 00-1.06-1.061l-2.476 2.475a.75.75 0 101.061 1.06l2.475-2.474zM4.745 15.39a.75.75 0 010 1.06l-1.06 1.06a.75.75 0 11-1.061-1.06l1.06-1.06a.75.75 0 011.061 0zm3.887 4.951a.75.75 0 00-1.06-1.06l-1.059 1.058a.75.75 0 001.06 1.06l1.06-1.058z" fill="#fff"/>
                    </svg>
                ),
                'officeCatchUpLogo':(
                    <svg width="24" height="24" fill="none">
                        <path d="M12.172,2 L12.2134,2.0023175 L12.2134,2.0023175 L12.313,2.014 C12.528,2.029 12.74,2.07 12.937,2.152 L13.0212,2.193085 L13.0212,2.193085 L13.2005,2.29207 L13.2005,2.29207 C13.2281,2.30548 13.2557,2.31894 13.281,2.336 L13.393125,2.421875 L13.393125,2.421875 L13.5363,2.54519 L13.5363,2.54519 L13.561325,2.5648225 L13.561325,2.5648225 L19.414,8.414 C19.789,8.789 20,9.298 20,9.828 L20,20 C20,21.104 19.104,22 18,22 L6,22 C4.896,22 4,21.104 4,20 L4,16.25 C4,15.8358 4.33579,15.5 4.75,15.5 C5.16421,15.5 5.5,15.8358 5.5,16.25 L5.5,20 C5.5,20.275 5.724,20.5 6,20.5 L18,20.5 C18.276,20.5 18.5,20.275 18.5,20 L18.5,10 L14,10 C12.896,10 12,9.104 12,8 L12,3.5 L6,3.5 C5.724,3.5 5.5,3.725 5.5,4 L5.5,9.25 C5.5,9.66421 5.16421,10 4.75,10 C4.33579,10 4,9.66421 4,9.25 L4,4 C4,2.896 4.896,2 6,2 L12.172,2 Z M8.13885,9.35798 L8.1949,9.4712 L10.6081,15.4977 L12.0348,13.3979 C12.1302857,13.2181571 12.2931429,13.0875653 12.4831312,13.0310621 L12.5909,13.0074 L12.6971,12.9999 L14.697,12.9999 C15.1112,12.9999 15.447,13.3357 15.447,13.7499 C15.447,14.08794 15.223278,14.373978 14.9155833,14.4676086 L14.7998,14.493 L14.697,14.4999 L13.1428,14.4999 L11.1641,17.5937 C10.9072467,18.0849133 10.246372,18.1211453 9.92221778,17.7257302 L9.85261,17.6251 L9.80375,17.524 L7.46737,11.6894 L6.4321,14.0511 C6.33564,14.2712 6.14148,14.4284 5.9136,14.4809 L5.85645,14.4918 L5.7452,14.5 L2.75,14.5 C2.33579,14.5 2,14.1642 2,13.75 C2,13.41196 2.2237139,13.125922 2.53142885,13.0323643 L2.64718,13.0069 L2.75,13 L5.25508,13 L6.81175,9.4489 C7.06044,8.88157 7.8355,8.85277 8.13885,9.35798 Z M13.5,4.621 L13.5,8 C13.5,8.275 13.724,8.5 14,8.5 L17.378,8.5 L13.5,4.621 Z" fill="#212121"></path>
                    </svg>
                ),
                'officeCatchUpFillLogo':(
                    <svg width="24" height="24" fill="none">
                        <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                            <g fill="url(#paint0_linear)" fill-rule="nonzero">
                                <path d="M12,2 L12,8.00395 C12.0020045,9.056575 12.8172167,9.91835816 13.8509078,9.99452724 L14,10 L20,10 L20,20 C20,21.0543909 19.18415,21.9181678 18.1492661,21.9945144 L18,22 L6,22 C4.94563773,22 4.08183483,21.18415 4.00548573,20.1492661 L4,20 L4,15.5 L5.78195,15.5 L5.9749,15.4858 L6.13806,15.4554 L6.13806,15.4554 C6.616455,15.34515 7.0313541,15.03726 7.27372077,14.6022243 L7.34797,14.4526 L7.42567,14.2754 L8.88833,17.928 L8.97129,18.0996 L8.99916,18.1463 C9.64855423,19.2361077 11.2340616,19.2883784 11.9447344,18.2343927 L12.0257,18.1026 L13.6902,15.4999 L14.7306,15.4999 L14.9083,15.488 L14.9596,15.4802 C15.8022,15.3528 16.447,14.6272 16.447,13.7499 C16.447,12.831725 15.7398912,12.0787075 14.8405274,12.0057012 L14.697,11.9999 L12.662,11.9999 L12.4778,12.0129 L12.4246,12.0213 C11.9645111,12.0937444 11.5518296,12.3471272 11.2790527,12.7243486 L11.1835,12.8715 L10.8376,13.3806 L9.11004,9.06651 L9.02482,8.89091 L8.99617,8.84319 C8.31159,7.70309 6.61089576,7.715519 5.96554251,8.90495285 L5.89588,9.04742 L4.60158,12 L4,12 L4,4 C4,2.94563773 4.81587733,2.08183483 5.85073759,2.00548573 L6,2 L12,2 Z M6.81175,9.4489 C7.04489687,8.91702813 7.74067363,8.85847363 8.0766877,9.26951216 L8.14458,9.36751 L8.1949,9.4712 L10.6081,15.4977 L12.0348,13.3979 C12.1302857,13.2181571 12.2931429,13.0875653 12.4831312,13.0310621 L12.5909,13.0074 L12.6971,12.9999 L14.697,12.9999 C15.1112,12.9999 15.447,13.3357 15.447,13.7499 C15.447,14.08794 15.223278,14.373978 14.9155833,14.4676086 L14.7998,14.493 L14.697,14.4999 L13.1428,14.4999 L11.1641,17.5937 C10.9072467,18.0849133 10.246372,18.1211453 9.92221778,17.7257302 L9.85261,17.6251 L9.80375,17.524 L7.46737,11.6894 L6.4321,14.0511 C6.34942,14.2397571 6.19496041,14.382202 6.00901184,14.4521117 L5.9136,14.4809 L5.85645,14.4918 L5.7452,14.5 L2.75,14.5 C2.33579,14.5 2,14.1642 2,13.75 C2,13.41196 2.2237139,13.125922 2.53142885,13.0323643 L2.64718,13.0069 L2.75,13 L5.25508,13 L6.81175,9.4489 Z M13.5,2.5 L19.5,8.5 L14,8.5 C13.7239,8.5 13.5,8.27614 13.5,8 L13.5,2.5 Z" id="🎨-Color"></path>
                            </g>
                        </g>
                        <defs>
                            <linearGradient id="paint0_linear" x1="17.005" y1="3.623" x2="7.675" y2="20.549" gradientUnits="userSpaceOnUse">
                                <stop offset=".011" stop-color="#FDB813"/>
                                <stop offset=".286" stop-color="#E27024"/>
                                <stop offset=".454" stop-color="#D74326"/>
                                <stop offset=".552" stop-color="#CF4B4C"/>
                                <stop offset=".645" stop-color="#C8526A"/>
                                <stop offset=".73" stop-color="#C25682"/>
                                <stop offset=".803" stop-color="#BD5993"/>
                                <stop offset=".863" stop-color="#B95B9E"/>
                                <stop offset=".901" stop-color="#B85CA3"/>
                            </linearGradient>
                        </defs>
                    </svg>
                ),
                'officeCatchUpDarkModeLogo':(
                    <svg width="24" height="24" fill="none">
                        <path d="M12.172,2 L12.2134,2.0023175 L12.2134,2.0023175 L12.313,2.014 C12.528,2.029 12.74,2.07 12.937,2.152 L13.0212,2.193085 L13.0212,2.193085 L13.2005,2.29207 L13.2005,2.29207 C13.2281,2.30548 13.2557,2.31894 13.281,2.336 L13.393125,2.421875 L13.393125,2.421875 L13.5363,2.54519 L13.5363,2.54519 L13.561325,2.5648225 L13.561325,2.5648225 L19.414,8.414 C19.789,8.789 20,9.298 20,9.828 L20,20 C20,21.104 19.104,22 18,22 L6,22 C4.896,22 4,21.104 4,20 L4,16.25 C4,15.8358 4.33579,15.5 4.75,15.5 C5.16421,15.5 5.5,15.8358 5.5,16.25 L5.5,20 C5.5,20.275 5.724,20.5 6,20.5 L18,20.5 C18.276,20.5 18.5,20.275 18.5,20 L18.5,10 L14,10 C12.896,10 12,9.104 12,8 L12,3.5 L6,3.5 C5.724,3.5 5.5,3.725 5.5,4 L5.5,9.25 C5.5,9.66421 5.16421,10 4.75,10 C4.33579,10 4,9.66421 4,9.25 L4,4 C4,2.896 4.896,2 6,2 L12.172,2 Z M8.13885,9.35798 L8.1949,9.4712 L10.6081,15.4977 L12.0348,13.3979 C12.1302857,13.2181571 12.2931429,13.0875653 12.4831312,13.0310621 L12.5909,13.0074 L12.6971,12.9999 L14.697,12.9999 C15.1112,12.9999 15.447,13.3357 15.447,13.7499 C15.447,14.08794 15.223278,14.373978 14.9155833,14.4676086 L14.7998,14.493 L14.697,14.4999 L13.1428,14.4999 L11.1641,17.5937 C10.9072467,18.0849133 10.246372,18.1211453 9.92221778,17.7257302 L9.85261,17.6251 L9.80375,17.524 L7.46737,11.6894 L6.4321,14.0511 C6.33564,14.2712 6.14148,14.4284 5.9136,14.4809 L5.85645,14.4918 L5.7452,14.5 L2.75,14.5 C2.33579,14.5 2,14.1642 2,13.75 C2,13.41196 2.2237139,13.125922 2.53142885,13.0323643 L2.64718,13.0069 L2.75,13 L5.25508,13 L6.81175,9.4489 C7.06044,8.88157 7.8355,8.85277 8.13885,9.35798 Z M13.5,4.621 L13.5,8 C13.5,8.275 13.724,8.5 14,8.5 L17.378,8.5 L13.5,4.621 Z" fill="#fff"></path>
                    </svg>
                ),
                'M365-apps':(
                    <svg width="24" height="24" fill="none">
                        <text x="5" y="15" fill="#FF0000">...</text>
                    </svg>
                ),
            }
        });
        /* tslint:enable */

        const componentProps = this.officeStartBootstrapperService.bootstrapOfficeStart();
        initializeFluentIcons(this.capabilitiesService, this.contextService, clientStartupConfigService);

        this.m365AppsEnabled = this.capabilitiesService.capabilities.m365AppsEnabled;
        if (this.m365AppsEnabled) {
            this.m365PinnedApps = this.m365PinnedAppService.getPinnedApps().map(id => id.toLowerCase());
        }

        const allWorkloadsAsync = this.workloadsService.getWorkloads();
        const workloadAsync = this.workloadsService.getCoreSegmentAndFrequentApps();
        const pwaAdditionalWorkloads = this.workloadsService.getAdditionalWorkloads();
        this.workloadsPromise = Promise.all([workloadAsync, pwaAdditionalWorkloads]).then(([workloads, additionalWorkloads]) => {
            const coreWorkloads = workloads.filter(workload => this.coreWorkloadIds.indexOf(workload.Id) >= 0);
            const nonCoreWorkloads = workloads.filter(workload => this.coreWorkloadIds.indexOf(workload.Id) < 0); // TODO: make responsive to window changes
            const nonCoreAdditionalTiles = additionalWorkloads.map(tile => this.convertWorkloadToTile(tile));
            const nonCoreTiles = nonCoreWorkloads.map(tile => this.convertWorkloadToTile(tile)).concat(nonCoreAdditionalTiles);
            this.coreAppTiles = coreWorkloads.map(this.convertWorkloadToTile.bind(this));
            this.otherAppTiles = this.otherAppTiles.concat(nonCoreTiles);
            this.shouldShowCallout = coreWorkloads.length > 0;
            if (this.m365AppsEnabled) {
                // TODO: Should we await this or call render on completion?
                this.setupPinnedApps();
            }
            return [...coreWorkloads, ...nonCoreWorkloads];
        });

        this.defaultSelectionSubscription = combineLatest(this.officeRouteService.getRouteChangeEndObservable(), from(workloadAsync)).subscribe(([routeEvent, workloads]) => {
            const currentRouteItem = routeEvent.currentRoute;
            if (currentRouteItem.route === OfficeRoute.OfficeHome || currentRouteItem.route === OfficeRoute.Pwa) {
                this.defaultSelectedTile = 'left-nav-home';
            } else if (currentRouteItem.route === OfficeRoute.AppGallery) {
                this.defaultSelectedTile = 'left-nav-allApps';
            } else if (currentRouteItem.route === OfficeRoute.SpaceGallery || currentRouteItem.route === OfficeRoute.SpaceContent) {
                this.defaultSelectedTile = 'left-nav-spaces';
            } else if (currentRouteItem.route === OfficeRoute.Start) {
                this.defaultSelectedTile = workloads.find(workload => currentRouteItem.app === workload.FileType)?.Id;

                // Adding the hosted app to the left rail if it does not already exist.
                if (!this.defaultSelectedTile) {
                    allWorkloadsAsync.then((allWorkloads: Array<Workload>) => {
                        const newWorkload = allWorkloads.find(workload => currentRouteItem.app === workload.FileType);
                        if (newWorkload) {
                                if (!this.otherAppTiles.find(tile => tile.id === newWorkload.Id + '_link')) {
                                this.otherAppTiles = [...this.otherAppTiles, this.convertWorkloadToTile(newWorkload)];
                            }
                            this.defaultSelectedTile = newWorkload.Id + '_link';
                        }
                    });
                } else {
                    // Add the "_link" to the defaultSelectedTile since the Id of the tile has _link.
                    this.defaultSelectedTile = this.defaultSelectedTile + '_link';
                }
            } else if (currentRouteItem.route === OfficeRoute.Content && (currentRouteItem.app === FileType.Fluid || currentRouteItem.app === FileType.FluidActivityBlueprint)) {
                this.defaultSelectedTile = '';
            }
            this.render();
        });
        this.createPanelEnabled = this.capabilitiesService.capabilities.leftNavReact && this.capabilitiesService.capabilities.createDocumentSupported;
        const homeOnClick = () => this.ngZone.run(() => {
            this.instrumentTileClick('left-nav-home');
            if (this.capabilitiesService.capabilities.isPwaInUwp) {
                this.officeRouteService.navigateTo({ route: OfficeRoute.Pwa });
            } else {
                this.officeRouteService.navigateTo({ route: OfficeRoute.OfficeHome });
            }
        });

        const homeTile = { id: 'left-nav-home', onClick: homeOnClick, icon: 'homeLogo', toolTip: this.locService.LeftNavHomeTileToolTip, selectedStateIcon: 'homeFillLogo', darkModeIcon: 'homeDarkModeLogo', ariaRole: 'button'};
        this.nonAppTiles = [homeTile];

        if (this.capabilitiesService.capabilities.spacesEnabled) {
            const spacesOnClick = () => {
                this.instrumentTileClick('left-nav-spaces');
                this.officeRouteService.navigateTo({ route: OfficeRoute.SpaceGallery });
            };
            this.nonAppTiles = [...this.nonAppTiles, { id: 'left-nav-spaces', onClick: spacesOnClick, icon: 'spacesLogo', selectedStateIcon: 'spacesFillLogo', darkModeIcon: 'spacesDarkModeLogo', toolTip: 'Spaces', ariaRole: 'button' }];
        }

        // stayingAwareEnabled - off for MSFT and Pilot
        if (this.capabilitiesService.capabilities.stayingAwareEnabled && this.deviceDetector.deviceInfo.browser.browserType !== BrowserType.IE) {
            import(/* webpackChunkName: "axios-interceptors" */ '../service-worker/setupAxiosInterceptors').then(mod => {
                mod.setupInterceptors(globalErrorHandler, instrumentationService, serviceWorkerConfigService, serviceWorkerEnabled, serviceWorkerStateService, serviceWorkerUserIdFactory, decryptResponseService);
            }).then(() => {
                if (!this.capabilitiesService.capabilities.stayingAwareRQEnabled) {
                    const stayingAwareBadgeSubject: Subject<string> = new ReplaySubject<string>(1);
                    import(
                        /*webpackChunkName: "staying-aware-data-service" */ '@office-start/staying-aware-data-service'
                    ).then(mod => {
                        // TODO: Refactor async flow in office-start to remove need for toPromise()
                        mod.subscribeStayingAwareData(componentProps, false).toPromise();
                        this.stayingAwareBadgeSubscription = mod.subscribeStayingAwareBadge().subscribe(
                            count => stayingAwareBadgeSubject.next(count)
                        );
                    });
                    // TODO: Preload component with scheduling
                    // TODO: Loading UI
                    const renderStayingAware = () =>
                    <React.Suspense fallback={<></>}>
                        <StayingAwareLazy {...componentProps}/>
                    </React.Suspense>;
                    this.nonAppTiles = [...this.nonAppTiles, { id: 'left-nav-catchup', icon: 'officeCatchUpLogo', toolTip: this.locService.LeftNavCatchUpTileToolTip, renderPanel: renderStayingAware, selectedStateIcon: 'officeCatchUpFillLogo', darkModeIcon: 'officeCatchUpDarkModeLogo', ariaRole: 'button', badgeObservable: stayingAwareBadgeSubject.asObservable()}];
                } else {
                    // TODO: Preload component with scheduling
                    // TODO: Loading UI
                    const renderStayingAware = () =>
                        <React.Suspense fallback={<></>}>
                            <StayingAwareRQLazy />
                        </React.Suspense>;
                    this.nonAppTiles = [...this.nonAppTiles, { id: 'left-nav-catchup', icon: 'officeCatchUpLogo', toolTip: this.locService.LeftNavCatchUpTileToolTip, renderPanel: renderStayingAware, selectedStateIcon: 'officeCatchUpFillLogo', darkModeIcon: 'officeCatchUpDarkModeLogo', ariaRole: 'button', badgeObservable: undefined, badgeElement: <StayingAwareBadgeLazy/>}];
                }
                this.render();
            });
        }

        if (capabilitiesService.capabilities.m365AppsEnabled) {
            const navigateToM365AppsHome = () =>  {
                this.officeRouteService.navigateTo({ route: OfficeRoute.M365Apps});
            };
            this.nonAppTiles = [...this.nonAppTiles, { id: 'M365-apps', onClick: navigateToM365AppsHome, icon: 'M365-apps', toolTip: this.locService.M365AppsTitle}];
        }

        // TODO: Should this wait for all nonappTiles or just create? Should loading tiles have a spinner or something? Would require error states.
        this.nonAppsPromise = new Promise<void>(resolve => {
            if (!this.createPanelEnabled) {
                resolve();
                return;
            }

            if (this.capabilitiesService.capabilities.createCenterEnabled) {
                this.createDocumentService.getCreateLinks(KnownAreas.CreateCenter).then(createLinks => {
                    if (createLinks && createLinks.length > 0) {
                        const createOnClick = () => {
                            this.instrumentTileClick('left-nav-create-center');
                            this.officeRouteService.navigateTo({ route: OfficeRoute.Create });
                        };

                        this.nonAppTiles = [
                            this.nonAppTiles[0],
                            { id: 'left-nav-create-center', onClick: createOnClick, icon: 'createNewLogo', selectedStateIcon: 'createNewFillLogo', darkModeIcon: 'createDarkModeLogo', toolTip: this.locService.LeftNavCreateTileToolTip, ariaRole: 'button' },
                            ...this.nonAppTiles.slice(1)
                        ];
                    }
                    resolve();
                }).catch(() => {
                    resolve();
                });
                return;
            }

            const addCreateToLeftNav = (createRenderFunc: any) => {
                this.nonAppTiles = [
                    this.nonAppTiles[0],
                    { id: 'left-nav-create', icon: 'createNewLogo', toolTip: this.locService.LeftNavCreateTileToolTip, renderPanel: createRenderFunc, selectedStateIcon: 'createNewFillLogo', darkModeIcon: 'createDarkModeLogo', ariaRole: 'button'},
                    ...this.nonAppTiles.slice(1)
                ];
                resolve();
            };

            this.createDocumentService.getCreateLinks(KnownAreas.LeftNav).then(createLinks => {
                if (createLinks.length === 0) {
                    resolve();
                    return;
                }

                const generateLinkGroups = (closePanel?: () => void) => {
                    const links = createLinks.map(link => {
                        const onClickFunc = () => this.ngZone.run(() => {
                            if (!!closePanel) {
                                closePanel();
                            }

                            const documentType = FileType[link.workload.FileType].toLowerCase();
                            // Only wait to log click if we open in a new tab, otherwise we risk losing the event
                            const logResultWithClick = this.contextService.sharedContext.defaultLinkTarget === LinkTarget.NewTab;
                            if (!logResultWithClick) {
                                this.instrumentCreateLinkClick(link.getId(), link.workload.Referral, KnownReferralPlatform.Web, documentType);
                            }

                            if (link.appName === 'Trello') {
                                // TODO: Once the app in Teams SDK is ready, add the deep link to task module here.
                                return;
                            }

                            // TODO: handle PWA Excel create flow
                            if (link.workload.IsThirdParty || link.createFromManifest) {
                                this.createDocumentService.createItemFromManifest(link.workload, (result: CreateDocumentResult) => {
                                    const referral = result.type === CreateDocumentResultType.Error ? '' : link.workload.Referral;
                                    if (logResultWithClick) {
                                        this.instrumentCreateLinkClick(link.getId(), referral, result.platform, documentType);
                                    }
                                    this.instrumentCreateLinkClickResult(link.getId(), result, documentType);
                                });
                            } else {
                                this.createDocumentService.createItem(link.creationFileType, link.url, null, (result: CreateDocumentResult) => {
                                    const referral = result.type === CreateDocumentResultType.Error ? '' : link.workload.Referral;
                                    if (logResultWithClick) {
                                        this.instrumentCreateLinkClick(link.getId(), referral, result.platform, documentType);
                                    }
                                    this.instrumentCreateLinkClickResult(link.getId(), result, documentType);
                                }, null, null, null, KnownAreas.LeftNav);
                            }
                        });
                        return ({
                            id: link.getId(),
                            ariaLabel: link.ariaLabel,
                            title: link.title,
                            fileTypeNameLocalized: link.fileTypeName,
                            appName: link.appName,
                            creationFileType: FileTypeReact[FileType[link.creationFileType]],
                            icon: link.icon,
                            iconUrl: link.iconUrl,
                            url: link.url,
                            onClick: onClickFunc,
                            workload: {
                                id: link.workload.getId(),
                                app: link.workload.Text,
                                color: link.workload.Color,
                                text: link.workload.Text,
                                title: link.workload.Title,
                                url: link.workload.Url
                            }
                        });
                    });
                    return [links];
                };
                const createRenderFunc = (closePanel?: () => void) => {
                    const linkGroups = generateLinkGroups(closePanel);
                    this.instrumentCreatePanelImpression(linkGroups);
                    return <CreateControl
                                createLinkGroups={linkGroups}
                                createHeaderText={this.locService.CreateFlyOutHeader}
                                darkMode={this.isDarkMode}
                                onClose={closePanel}
                                useSvgIcons={this.capabilitiesService.capabilities.createPaneSvgsEnabled} />;
                };
                addCreateToLeftNav(createRenderFunc);
            }).catch(() => { resolve(); });
        });
        const appGalleryOnClick = () => this.ngZone.run(() => {
            this.instrumentTileClick('left-nav-allApps');
            this.officeRouteService.navigateTo({ route: OfficeRoute.AppGallery });
        });
        this.allAppsTile = {
            id: 'left-nav-allApps',
            onClick: appGalleryOnClick,
            icon: 'allAppsLogo',
            toolTip: this.locService.LeftNavAllAppsTileToolTip,
            selectedStateIcon: 'allAppsFillLogo',
            darkModeIcon: 'allAppsDarkModeLogo',
            ariaRole: 'button'
        };
        if (this.capabilitiesService.capabilities.darkModeEnabled) {
            this.darkModeSubscription = this.themingService.darkModeSubscription.subscribe(event => {
                if (typeof event !== 'undefined') {
                    this.isDarkMode = event;
                    componentProps.isDarkMode = this.isDarkMode;
                    this.render();
                }
            });
        }

        // Set the icons correctly in high contrast setting
        this.setShouldChangeInHighContrast();
    }

    public ngOnInit() {
        super.ngOnInit();
    }

    public ngAfterViewChecked() {
        if (!this.capabilitiesService.capabilities.leftNavCalloutEnabled || !this.shouldShowCallout || this.leftNavCalloutPromise) {
            return;
        }
        this.leftNavCalloutPromise = this.userProfileService.doesMetricExist(KnownInstrumentationEvents.LeftNavCalloutImpression, true);
        this.leftNavCalloutPromise.then(sawLeftNavCallout => {
            if (sawLeftNavCallout) {
                return;
            }
            if (this.userProfileService.isFirstVisit() || (this.userProfileService.fullDaysSinceFirstVisit() >= 0 && this.userProfileService.fullDaysSinceFirstVisit() < 14)) {
                this.userProfileService.incrementMetric(KnownInstrumentationEvents.LeftNavCalloutImpression);
            } else {
                // Check if the user is an admin. If admin, the first callout will anchor to the admin tile, if it is visible in the viewport
                let appsOnLeftAnchor = undefined;
                if (this.userContextService.user.isAdmin) {
                    appsOnLeftAnchor = this.documentService.nativeDocument.getElementById('ShellAdmin_link');
                }
                if (!appsOnLeftAnchor || !this.isElementInViewport(appsOnLeftAnchor)) {
                    appsOnLeftAnchor = this.documentService.nativeDocument.getElementById('ShellMail_link') || this.documentService.nativeDocument.getElementById('ShellWordOnline_link');
                }
                if (appsOnLeftAnchor) {
                    this.featureCalloutService.addCallout(new FeatureCallout(
                        appsOnLeftAnchor,
                        CalloutType.LeftNav,
                        KnownAreas.LeftNavCallout,
                        this.locService.LeftNavInitialCalloutHeader,
                        this.locService.LeftNavAppsOnLeftCalloutBody,
                        ['left-nav-teaching-callout', 'ms-bgc-w'],
                        null,
                        null,
                        false,
                        true,
                        true
                    ));
                }
                const documentsAnchor = this.documentService.nativeDocument.getElementById('recommendedHeader') || this.documentService.nativeDocument.getElementById('tab-list-header-link-unpinned-mru') || this.documentService.nativeDocument.getElementById('edgeworth-AllFiles-tab');
                if (documentsAnchor) {
                    this.featureCalloutService.addCallout(new FeatureCallout(
                        documentsAnchor,
                        CalloutType.LeftNav,
                        KnownAreas.LeftNavCallout,
                        this.locService.LeftNavDocumentsCalloutHeader,
                        this.locService.LeftNavDocumentsCalloutBody,
                        null,
                        null,
                        null,
                        false,
                        true,
                        true
                    ));
                }
                const createTileElement = this.documentService.nativeDocument.getElementById('left-nav-create');
                if (createTileElement) {
                    this.featureCalloutService.addCallout(new FeatureCallout(
                        createTileElement,
                        CalloutType.LeftNav,
                        KnownAreas.LeftNavCallout,
                        this.locService.LeftNavCreateCalloutHeader,
                        this.locService.LeftNavCreateCalloutBody,
                        ['left-nav-teaching-callout', 'ms-bgc-w'],
                        null,
                        null,
                        false,
                        true,
                        true
                    ));
                }
                const allAppsTileElement = this.documentService.nativeDocument.getElementById('left-nav-allApps');
                if (allAppsTileElement) {
                    this.featureCalloutService.addCallout(new FeatureCallout(
                        allAppsTileElement,
                        CalloutType.LeftNav,
                        KnownAreas.LeftNavCallout,
                        this.locService.LeftNavAllAppsCalloutHeader,
                        this.locService.LeftNavAllAppsCalloutBody,
                        ['left-nav-teaching-callout', 'ms-bgc-w'],
                        null,
                        null,
                        false,
                        true,
                        true,
                        null,
                        false,
                        null,
                        0,
                        -70,
                        null,
                        65
                    ));
                }

                this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.LeftNavCalloutImpression, {}, [KnownInstrumentationEvents.LeftNavCalloutImpression]);
            }
        });
    }

    public ngOnDestroy() {
        super.ngOnDestroy();

        this.darkModeSubscription?.unsubscribe();
        this.defaultSelectionSubscription?.unsubscribe();
        this.stayingAwareBadgeSubscription?.unsubscribe();
    }

    public async getReactElement() {
        return this.workloadsPromise.then(workloads =>
            this.nonAppsPromise.then(() => {
                if (!this.loggedInstrumentation) {
                    this.logImpression();
                    this.performanceService.mark(KnownInstrumentationEvents.LeftNavReady);
                    this.performanceService.mark(KnownInstrumentationEvents.AppTilesReady);
                    this.browserPaintMeasurementService.recordWhenPainted(PaintMarkerName.LeftNav);
                    this.browserPaintMeasurementService.recordWhenPainted(PaintMarkerName.App_Tiles);

                    // TODO: Disable leftnav floodgate for now while investigating NPS issues
                    // this.floodgateService.recordFloodgate(['SawLeftNav']);
                    this.loggedInstrumentation = true;
                    let clickedOneApp = false;

                    workloads.forEach(workload => {
                        if (workload.ClickCount > 0 && this.capabilitiesService.capabilities.showFloodgate && this.capabilitiesService.capabilities.officeHomeFloodgate && !clickedOneApp) {
                            clickedOneApp = true;
                        }
                    });
                    if (clickedOneApp) {
                        this.floodgateService.recordFloodgate(['ClickedOneApp']);
                    }
                }
                return (
                    <LeftNavControl
                        nonAppTiles={this.nonAppTiles}
                        coreAppTiles={this.coreAppTiles}
                        otherAppTiles={this.otherAppTiles}
                        allAppsTile={this.allAppsTile}
                        darkMode={this.isDarkMode}
                        defaultSelectedId={this.defaultSelectedTile}
                        target={this.contextService.sharedContext.defaultLinkTarget}
                        highContrast={this.shouldChangeIconInHighContrast}
                    > </LeftNavControl>
                );
            })
        );
    }

    private convertWorkloadToTile(workload: Workload): LeftNavTileItem {
        return {
            id: `${workload.Id}_link`,
            svgClassName: workload.IconUnicodeCodepointCssClass,
            path: workload.Url,
            color: workload.Color,
            onClick: event => { this.ngZone.run(() => this.onAppClick(event.nativeEvent, workload)); },
            toolTip: workload.Text,
            iconImageUrl: workload.IsThirdParty ? workload.ThirdPartyIcon : undefined,
            ariaRole: workload.InAppNav ? 'button' : 'link',
            changeColorInDarkMode: workload.ChangeColorInDarkMode
        };
    }

    private getAppIndex(id: string) {
        return [...this.nonAppTiles, ...this.coreAppTiles,
            ...this.otherAppTiles, this.allAppsTile]
            .findIndex(tile => tile.id === id);
    }

    private onAppClick(event: Event, workload: Workload) {
        if (!workload.Url || workload.InAppNav) {
            if (event) {
                event.preventDefault();
            }
            if (!workload.Url) {
                return;
            }
        }
        const logAfterResult = this.contextService.sharedContext.defaultLinkTarget === LinkTarget.NewTab;
        if (!logAfterResult) {
            this.workloadsService.pinWorkload(workload);
            this.instrumentAppTileClick(workload, AppLaunchResult.Web);
        }
        this.appLauncherService.launchApp(workload, event, LaunchAction.OpenAppTile)
            .then(result => {
                if (logAfterResult) {
                    this.workloadsService.pinWorkload(workload);
                    this.instrumentAppTileClick(workload, result);
                }
            });
    }

    private instrumentAppTileClick(workload: Workload, launchResult?: AppLaunchResult) {
        const referral = launchResult === AppLaunchResult.Failure ? '' : workload.Referral;
        const componentArea = KnownAreas.LeftNav;
        const index = this.getAppIndex(workload.Id);

        const params: InstrumentationParams = {};
        params[InstrumentationParam.ReferralType] = this.workloadsService.getReferralType(workload).toString();
        params[InstrumentationParam.Index] = index.toString();

        this.instrumentationService
            .logClickEvent(KnownInstrumentationEvents.GenericClick, workload.AppId, componentArea, referral, launchResult === AppLaunchResult.Native ? KnownReferralPlatform.Native : KnownReferralPlatform.Web, params, [], DataSecurityLevel.Security, workload.InAppNav);
        this.eventService.broadcastEvent({
            name: AngularEvents.appTileClick,
            args: [workload.Id]
        });
    }

    private instrumentTileClick(id: string) {
        const index = this.getAppIndex(id);
        const params: InstrumentationParams = {};
        params[InstrumentationParam.Index] = index.toString();
        this.instrumentationService
        .logClickEvent(KnownInstrumentationEvents.GenericClick, id, KnownAreas.LeftNav, null, null, params, [], DataSecurityLevel.Security, true);

        this.eventService.broadcastEvent({
            name: AngularEvents.appTileClick,
            args: [id]
        });
    }

    private instrumentCreateLinkClick(workloadId: string, referral: string, referralPlatform: KnownReferralPlatform, documentType: string) {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.DocumentType] = documentType;
        this.instrumentationService.logClickEvent(KnownInstrumentationEvents.CreateLinkClick, workloadId, KnownAreas.LeftNav, referral, referralPlatform, params);
    }

    private instrumentCreateLinkClickResult(workloadId: string, result: CreateDocumentResult, documentType: string) {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.ElementId] = workloadId;
        params[InstrumentationParam.Action] = result.type;
        params[InstrumentationParam.Latency] = result.latency.toString();
        params[InstrumentationParam.Area] = KnownAreas.LeftNav;
        params[InstrumentationParam.DocumentType] = documentType;
        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.CreateLinkClickResult, params);
    }

    private instrumentCreatePanelImpression(createLinkGroups: Array<Array<any>>) {
        let links = [];
        createLinkGroups.forEach(group => {
            links = links.concat(group);
        });
        const params: InstrumentationParams = {};
        params[InstrumentationParam.Distribution] = links.map(link => link.appName).toString();
        params[InstrumentationParam.RenderSize] = (links.length).toString();
        this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.CreatePanelImpression, params);
    }

    private logImpression() {
        const numAppsFittingWindow = (this.windowService.nativeWindow.innerHeight - 48) / 48; //48px for one tile
        const params: InstrumentationParams = {};
        const allTiles = [...this.nonAppTiles, ...this.coreAppTiles, ...this.otherAppTiles, this.allAppsTile];
        params[InstrumentationParam.ActualSize] = (allTiles.length).toString();
        params[InstrumentationParam.Distribution] = allTiles.map(tile => tile.id).toString();
        params[InstrumentationParam.RenderSize] = Math.min(Math.floor(numAppsFittingWindow), allTiles.length).toString();

        this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.LeftNavImpression, params);
    }

    private async setupPinnedApps(): Promise<void> {
        const pinnedTiles = [];
        for (const app of this.m365PinnedApps) {
            try {
                const teamsAppsClick = () => this.ngZone.run(() => this.officeRouteService.navigateTo({ route: OfficeRoute.M365Apps, app: FileItem.fileTypeNameToFileType(app) }, true));
                const appManifest = await this.m365PinnedAppService.getPinnedAppManifest(app);
                pinnedTiles.push({ id: app, onClick: teamsAppsClick, toolTip: appManifest.name, iconImageUrl: appManifest.largeImageUrl });
            } catch {
                // TODO: Log error
            }
        }

        this.otherAppTiles = this.otherAppTiles.concat(pinnedTiles);
    }

    private isElementInViewport (element: Element) {
        const rect = element.getBoundingClientRect();
        return (rect.top + rect.height < this.windowService.nativeWindow.innerHeight);
    }

    private setShouldChangeInHighContrast() {
        // In HighContrastBlack (white on black) - we want to change the icon
        const mqlHighContrast = window.matchMedia('(-ms-high-contrast: white-on-black)');
        const highContrastSetting = () => {
            this.shouldChangeIconInHighContrast = mqlHighContrast.matches;
            this.render();
        };
        // Call the function on load
        highContrastSetting();
        // add listeners for between session changes
        mqlHighContrast.addListener(highContrastSetting);
    }
}
